import React, { useState } from 'react';
import { X, Sparkles, Loader2 } from 'lucide-react';
import { aiApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const SAMPLES = [
  'Senior React.js developer with 5+ years of experience building production web apps. Strong TypeScript, state management (Redux/Zustand), performance, and testing skills required.',
  'Sales Development Representative for a B2B SaaS startup. Looking for excellent written and verbal communication, CRM hygiene, prospecting, and resilience to handle outbound at scale.',
  'Data engineer with deep SQL, Python, and cloud-warehouse (Snowflake/BigQuery) experience. Builds reliable ELT pipelines and partners with analytics teams on data quality.',
];

export default function DraftInterviewModal({ open, onClose, onApply }) {
  const { toast } = useToast();
  const [jd, setJd] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const generate = async () => {
    if (jd.trim().length < 30) {
      toast({ title: 'Add a bit more detail', description: 'Paste at least a 30-character job description so AI can tailor the interview.' });
      return;
    }
    setLoading(true);
    try {
      const data = await aiApi.draftInterview({ job_description: jd.trim() });
      onApply(data);
      toast({ title: 'Interview drafted', description: `Loaded "${data.name}" with ${data.flow.length} modules.` });
      onClose();
      setJd('');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'AI service is unavailable.';
      toast({ title: 'Draft failed', description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="draft-modal">
      <div className="absolute inset-0 bg-[#1A1533]/60 backdrop-blur-sm" onClick={loading ? undefined : onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[94vw] max-w-[640px] max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 md:px-6 pt-5 md:pt-6 pb-3 border-b border-[#EEE9FB]">
          <div>
            <h2 className="text-[17px] md:text-[19px] font-bold text-[#1A1533] inline-flex items-center gap-2">
              <Sparkles size={18} className="text-[#5B4FE8]" />
              Draft an interview with AI
            </h2>
            <p className="text-[12.5px] text-[#4A4463] mt-1 leading-relaxed">
              Paste a job description. Zara will design a complete interview flow — name, opening, 2 skill conversations with 5 questions each, a 5-MCQ knowledge test, and a coding exercise.
            </p>
          </div>
          <button
            onClick={loading ? undefined : onClose}
            disabled={loading}
            className="w-8 h-8 rounded-full bg-white hover:bg-[#F1ECFE] flex items-center justify-center text-[#4A4463] disabled:opacity-50 shrink-0"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-4">
          <textarea
            value={jd}
            onChange={(e) => setJd(e.target.value)}
            disabled={loading}
            rows={9}
            placeholder="Paste the full job description here…"
            data-testid="draft-jd-input"
            className="w-full px-3.5 py-3 rounded-xl border border-[#ECE6FA] bg-[#FAF8FF] outline-none text-[13.5px] placeholder-[#9A93B0] focus:ring-2 focus:ring-[#5B4FE8]/30 focus:bg-white resize-none disabled:opacity-60"
          />
          <div className="mt-3">
            <p className="text-[11.5px] font-semibold text-[#8A83A5] uppercase tracking-wide mb-1.5">Try a sample</p>
            <div className="flex flex-wrap gap-1.5">
              {SAMPLES.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setJd(s)}
                  disabled={loading}
                  className="text-[11.5px] px-3 py-1.5 rounded-full bg-[#F1ECFE] text-[#5B4FE8] hover:bg-[#E9E3FB] font-semibold disabled:opacity-50"
                >
                  {['React Dev', 'SDR', 'Data Engineer'][i]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-[#EEE9FB] px-5 md:px-6 py-3 flex items-center justify-end gap-2 flex-wrap">
          <p className="text-[11.5px] text-[#8A83A5] mr-auto">
            Replaces the current draft. Takes 20–40 seconds.
          </p>
          <button
            onClick={loading ? undefined : onClose}
            disabled={loading}
            className="px-4 py-2 rounded-full text-[13px] font-semibold text-[#4A4463] hover:bg-[#F1ECFE] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={generate}
            disabled={loading || jd.trim().length < 30}
            data-testid="draft-generate-btn"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#5B4FE8] to-[#7A6BFF] text-white text-[13px] font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loading ? 'Drafting…' : 'Generate interview'}
          </button>
        </div>
      </div>
    </div>
  );
}
